import { config } from "../config/environment";
import type {
  BodyMeasurement,
  CreateBodyMeasurementRequest,
  UpdateBodyMeasurementRequest,
  GetBodyMeasurementsResponse,
  GetBodyMeasurementResponse,
  CreateBodyMeasurementResponse,
  UpdateBodyMeasurementResponse,
  DeleteBodyMeasurementResponse,
  BodyMeasurementsSummary
} from "../types/bodyMeasurements";

const API_BASE_URL = config.apiUrl;

console.log("[bodyMeasurementsService] Initialized with API_BASE_URL:", API_BASE_URL);

const getAuthToken = (): string => {
  let token = localStorage.getItem("authToken");
  
  if (!token) {
    token = localStorage.getItem("token");
  }
  
  if (!token) {
    console.warn("[bodyMeasurementsService] No auth token found in localStorage");
    throw new Error("인증 토큰이 없습니다. 다시 로그인해주세요.");
  }
  
  return token;
};

/**
 * 신체정보 목록 조회
 */
export const getBodyMeasurements = async (
  pageNum: number = 1,
  pageSize: number = 10
): Promise<GetBodyMeasurementsResponse> => {
  try {
    console.log("[bodyMeasurementsService] getBodyMeasurements - Page:", pageNum, "Size:", pageSize);
    
    const response = await fetch(
      `${API_BASE_URL}/health/body-measurements?pageNum=${pageNum}&pageSize=${pageSize}`,
      {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${getAuthToken()}`,
        },
      }
    );

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || "신체정보 조회 실패");
    }

    const data = await response.json();
    console.log("[bodyMeasurementsService] getBodyMeasurements - Success:", data.data.length, "records");
    return data;
  } catch (error) {
    console.error("[bodyMeasurementsService] getBodyMeasurements - Error:", error);
    throw error;
  }
};

/**
 * 신체정보 단일 조회
 */
export const getBodyMeasurement = async (id: string): Promise<GetBodyMeasurementResponse> => {
  try {
    console.log("[bodyMeasurementsService] getBodyMeasurement - ID:", id);
    
    const response = await fetch(
      `${API_BASE_URL}/health/body-measurements/${id}`,
      {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${getAuthToken()}`,
        },
      }
    );

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || "신체정보 조회 실패");
    }

    const data = await response.json();
    console.log("[bodyMeasurementsService] getBodyMeasurement - Success");
    return data;
  } catch (error) {
    console.error("[bodyMeasurementsService] getBodyMeasurement - Error:", error);
    throw error;
  }
};

/**
 * 신체정보 생성 (직접입력 또는 동기화)
 */
export const createBodyMeasurement = async (
  data: CreateBodyMeasurementRequest
): Promise<CreateBodyMeasurementResponse> => {
  try {
    console.log("[bodyMeasurementsService] createBodyMeasurement - Data source:", data.data_source);
    
    const response = await fetch(
      `${API_BASE_URL}/health/body-measurements`,
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${getAuthToken()}`,
        },
        body: JSON.stringify(data),
      }
    );

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || "신체정보 등록 실패");
    }

    const result = await response.json();
    console.log("[bodyMeasurementsService] createBodyMeasurement - Success:", result.data.id);
    return result;
  } catch (error) {
    console.error("[bodyMeasurementsService] createBodyMeasurement - Error:", error);
    throw error;
  }
};

/**
 * 신체정보 수정
 */
export const updateBodyMeasurement = async (
  id: string,
  data: UpdateBodyMeasurementRequest
): Promise<UpdateBodyMeasurementResponse> => {
  try {
    console.log("[bodyMeasurementsService] updateBodyMeasurement - ID:", id);
    
    const response = await fetch(
      `${API_BASE_URL}/health/body-measurements/${id}`,
      {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${getAuthToken()}`,
        },
        body: JSON.stringify(data),
      }
    );

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || "신체정보 수정 실패");
    }

    const result = await response.json();
    console.log("[bodyMeasurementsService] updateBodyMeasurement - Success");
    return result;
  } catch (error) {
    console.error("[bodyMeasurementsService] updateBodyMeasurement - Error:", error);
    throw error;
  }
};

/**
 * 신체정보 삭제
 */
export const deleteBodyMeasurement = async (id: string): Promise<DeleteBodyMeasurementResponse> => {
  try {
    console.log("[bodyMeasurementsService] deleteBodyMeasurement - ID:", id);
    
    const response = await fetch(
      `${API_BASE_URL}/health/body-measurements/${id}`,
      {
        method: "DELETE",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${getAuthToken()}`,
        },
      }
    );

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || "신체정보 삭제 실패");
    }

    const result = await response.json();
    console.log("[bodyMeasurementsService] deleteBodyMeasurement - Success");
    return result;
  } catch (error) {
    console.error("[bodyMeasurementsService] deleteBodyMeasurement - Error:", error);
    throw error;
  }
};

/**
 * 신체정보 대시보드 요약 데이터 조회 (최신 데이터 및 평균값)
 */
export const getBodyMeasurementsSummary = async (): Promise<BodyMeasurementsSummary> => {
  try {
    console.log("[bodyMeasurementsService] getBodyMeasurementsSummary");
    
    const response = await fetch(
      `${API_BASE_URL}/health/body-measurements/summary/latest`,
      {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${getAuthToken()}`,
        },
      }
    );

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || "요약 데이터 조회 실패");
    }

    const apiResponse = await response.json();
    const data = apiResponse.data;

    // 트렌드 계산 (최신 데이터와 이전 데이터 비교)
    let trends = {
      weightTrend: null as 'up' | 'down' | 'stable' | null,
      bmiTrend: null as 'up' | 'down' | 'stable' | null,
      bodyFatTrend: null as 'up' | 'down' | 'stable' | null,
    };

    if (data.latest && data.totalRecords > 1) {
      // 이전 데이터 조회 (최신 데이터의 이전 기록)
      try {
        const allData = await getBodyMeasurements(1, data.totalRecords);
        if (allData.data.length > 1) {
          const previous = allData.data[1]; // 두 번째 최신 데이터
          const latest = allData.data[0]; // 최신 데이터

          // Weight 트렌드
          if (latest.weight && previous.weight) {
            if (latest.weight > previous.weight + 0.5) {
              trends.weightTrend = 'up';
            } else if (latest.weight < previous.weight - 0.5) {
              trends.weightTrend = 'down';
            } else {
              trends.weightTrend = 'stable';
            }
          }

          // BMI 트렌드
          if (latest.bmi && previous.bmi) {
            if (latest.bmi > previous.bmi + 0.3) {
              trends.bmiTrend = 'up';
            } else if (latest.bmi < previous.bmi - 0.3) {
              trends.bmiTrend = 'down';
            } else {
              trends.bmiTrend = 'stable';
            }
          }

          // Body Fat 트렌드
          if (latest.body_fat_percentage && previous.body_fat_percentage) {
            if (latest.body_fat_percentage > previous.body_fat_percentage + 0.5) {
              trends.bodyFatTrend = 'up';
            } else if (latest.body_fat_percentage < previous.body_fat_percentage - 0.5) {
              trends.bodyFatTrend = 'down';
            } else {
              trends.bodyFatTrend = 'stable';
            }
          }
        }
      } catch (err) {
        console.warn("[bodyMeasurementsService] Failed to calculate trends:", err);
      }
    }

    const summary: BodyMeasurementsSummary = {
      latest: data.latest,
      averageWeight: data.averageWeight,
      averageBMI: data.averageBMI,
      averageBodyFat: data.averageBodyFat,
      averageWaistCircumference: data.averageWaistCircumference,
      totalRecords: data.totalRecords,
      dateRange: data.dateRange,
      trends
    };

    console.log("[bodyMeasurementsService] getBodyMeasurementsSummary - Success");
    return summary;
  } catch (error) {
    console.error("[bodyMeasurementsService] getBodyMeasurementsSummary - Error:", error);
    throw error;
  }
};

/**
 * 여러 신체정보 데이터 일괄 생성 (동기화용)
 */
export const createMultipleBodyMeasurements = async (
  measurements: CreateBodyMeasurementRequest[]
): Promise<BodyMeasurement[]> => {
  try {
    console.log("[bodyMeasurementsService] createMultipleBodyMeasurements - Count:", measurements.length);
    
    const results: BodyMeasurement[] = [];
    let successCount = 0;
    let failureCount = 0;

    for (const measurement of measurements) {
      try {
        const response = await createBodyMeasurement(measurement);
        if (response.success) {
          results.push(response.data);
          successCount++;
        }
      } catch (error) {
        console.warn("[bodyMeasurementsService] Failed to create measurement:", error);
        failureCount++;
      }
    }

    console.log(
      "[bodyMeasurementsService] createMultipleBodyMeasurements - Success:",
      successCount,
      "Failed:",
      failureCount
    );
    
    return results;
  } catch (error) {
    console.error("[bodyMeasurementsService] createMultipleBodyMeasurements - Error:", error);
    throw error;
  }
};
